import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Collapse,
  Tooltip,
  Chip,
} from '@mui/material';
import { APP_CONFIG } from '../../api/config';
import {
  Dashboard as DashboardIcon,
  LibraryMusic,
  LocalOffer,
  CloudUpload,
  Analytics,
  Settings,
  ExpandMore,
  QueueMusic,
  Devices,
  Business,
  LocationOn,
  QrCode,
  NotificationsActive,
  Map,
  People,
  AdminPanelSettings,
  Security,
  History,
  MonitorHeart,
  Schedule,
  Layers,
  Extension,
  CalendarMonth,
  Radio,
  Room,
  AutoAwesome,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSongForgeAccess } from '../../hooks/useFeatureAccess';
import SidebarToggle from './SidebarToggle';

// Section color definitions
const SECTION_COLORS = {
  default: {
    main: '#4a9eff',
    bg: 'rgba(74, 158, 255, 0.12)',
    bgHover: 'rgba(74, 158, 255, 0.16)',
  },
  scheduling: {
    main: '#14b8a6',
    bg: 'rgba(20, 184, 166, 0.12)',
    bgHover: 'rgba(20, 184, 166, 0.16)',
  },
  fleet: {
    main: '#6366f1',
    bg: 'rgba(99, 102, 241, 0.12)',
    bgHover: 'rgba(99, 102, 241, 0.16)',
  },
  management: {
    main: '#10b981',
    bg: 'rgba(16, 185, 129, 0.12)',
    bgHover: 'rgba(16, 185, 129, 0.16)',
  },
  aiGeneration: {
    main: '#8b5cf6',
    bg: 'rgba(139, 92, 246, 0.12)',
    bgHover: 'rgba(139, 92, 246, 0.16)',
  },
};

interface SidebarProps {
  onItemSelect?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onItemSelect }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const { user } = useAuth();
  const isGlobalAdmin = user?.global_role === 'global_admin';
  const { hasAccess: hasSongForgeAccess } = useSongForgeAccess();
  const [schedulingOpen, setSchedulingOpen] = React.useState(location.pathname.startsWith('/scheduling'));
  const [fleetOpen, setFleetOpen] = React.useState(location.pathname.startsWith('/fleet'));
  const [managementOpen, setManagementOpen] = React.useState(location.pathname.startsWith('/management'));

  // Auto-close submenus when collapsed
  React.useEffect(() => {
    if (isCollapsed) {
      setSchedulingOpen(false);
      setFleetOpen(false);
      setManagementOpen(false);
    }
  }, [isCollapsed]);

  // Helper to navigate and close mobile drawer
  const handleNavigate = (path: string) => {
    navigate(path);
    onItemSelect?.();
  };

  const handleSchedulingClick = () => {
    if (isCollapsed) {
      handleNavigate('/scheduling/zones');
      return;
    }
    const newState = !schedulingOpen;
    setSchedulingOpen(newState);
    if (newState) {
      setFleetOpen(false);
      setManagementOpen(false);
    }
  };

  const handleFleetClick = () => {
    if (isCollapsed) {
      handleNavigate('/fleet');
      return;
    }
    const newState = !fleetOpen;
    setFleetOpen(newState);
    if (newState) {
      setSchedulingOpen(false);
      setManagementOpen(false);
    }
  };

  const handleManagementClick = () => {
    if (isCollapsed) {
      handleNavigate('/management/users');
      return;
    }
    const newState = !managementOpen;
    setManagementOpen(newState);
    if (newState) {
      setSchedulingOpen(false);
      setFleetOpen(false);
    }
  };


  const allMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Content Library', icon: <LibraryMusic />, path: '/content' },
    { text: 'Tags', icon: <LocalOffer />, path: '/content/tags', requiresGlobalAdmin: true },
    { text: 'Upload', icon: <CloudUpload />, path: '/upload', requiresGlobalAdmin: true },
    { text: 'SongForge', icon: <AutoAwesome />, path: '/songforge', requiresSongForge: true },
    { text: 'Scheduling', icon: <Schedule />, path: '/scheduling', hasSubmenu: true },
    { text: 'Fleet', icon: <Devices />, path: '/fleet', hasSubmenu: true },
    { text: 'Management', icon: <AdminPanelSettings />, path: '/management', hasSubmenu: true },
    { text: 'Analytics', icon: <Analytics />, path: '/analytics' },
    { text: 'Settings', icon: <Settings />, path: '/settings', requiresGlobalAdmin: true },
  ];

  // Filter menu items based on access permissions.
  // requiresGlobalAdmin gates platform-only items (e.g. SongForge until
  // it has per-org entitlement, system-level admin views) so customer
  // tenants don't see internal tooling in their sidebar.
  const menuItems = allMenuItems.filter((item) => {
    if (item.requiresSongForge && !hasSongForgeAccess) return false;
    if ((item as { requiresGlobalAdmin?: boolean }).requiresGlobalAdmin && !isGlobalAdmin) return false;
    return true;
  });

  const fleetSubmenu = [
    { text: 'Overview', icon: <Map />, path: '/fleet' },
    { text: 'Devices', icon: <Devices />, path: '/fleet/devices' },
    { text: 'Player Health', icon: <MonitorHeart />, path: '/fleet/player-health' },
    { text: 'Sites', icon: <LocationOn />, path: '/fleet/sites' },
    { text: 'Organizations', icon: <Business />, path: '/fleet/organizations' },
    { text: 'Provisioning', icon: <QrCode />, path: '/fleet/provisioning' },
    { text: 'Alerts', icon: <NotificationsActive />, path: '/fleet/alerts' },
  ];

  // Roles, Audit Log, and System Status are platform-level today: Roles
  // exposes the Global Admin role definition, Audit Log shows every
  // tenant's events (no org scoping yet), System Status surfaces
  // system-wide health. All three are Global-Admin-only until they get
  // proper org-scoped variants. Users stays available to org admins —
  // the API already filters that list to their org.
  const managementSubmenu = [
    { text: 'Users', icon: <People />, path: '/management/users' },
    ...(isGlobalAdmin
      ? [
          { text: 'Roles', icon: <Security />, path: '/management/roles' },
          { text: 'Audit Log', icon: <History />, path: '/management/audit' },
          { text: 'System Status', icon: <MonitorHeart />, path: '/system/status' },
        ]
      : []),
  ];

  const schedulingSubmenu = [
    { text: 'Zones', icon: <Room />, path: '/scheduling/zones' },
    { text: 'Segments', icon: <Extension />, path: '/scheduling/segments' },
    { text: 'Sequences', icon: <Layers />, path: '/scheduling/sequences' },
    { text: 'Playlists', icon: <QueueMusic />, path: '/scheduling/playlists' },
    { text: 'Schedules', icon: <CalendarMonth />, path: '/scheduling/schedules' },
    { text: 'Channels', icon: <Radio />, path: '/scheduling/channels' },
  ];


  const renderMenuItem = (item: typeof menuItems[0], colors: typeof SECTION_COLORS.default, isSelected: boolean) => {
    const content = (
      <ListItemButton
        selected={isSelected}
        onClick={() => handleNavigate(item.path)}
        sx={{
          mx: 1,
          borderRadius: '10px',
          minHeight: 44,
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          px: isCollapsed ? 1.5 : 2,
          '&.Mui-selected': {
            backgroundColor: colors.bg,
            '&:hover': {
              backgroundColor: colors.bgHover,
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        <ListItemIcon
          sx={{
            color: isSelected ? colors.main : 'text.secondary',
            minWidth: isCollapsed ? 0 : 40,
            justifyContent: 'center',
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            transition={{ duration: 0.2 }}
          >
            <ListItemText
              primary={item.text}
              sx={{
                '& .MuiListItemText-primary': {
                  color: isSelected ? colors.main : 'inherit',
                  fontWeight: isSelected ? 600 : 400,
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </motion.div>
        )}
      </ListItemButton>
    );

    if (isCollapsed) {
      return (
        <Tooltip title={item.text} placement="right" arrow>
          {content}
        </Tooltip>
      );
    }

    return content;
  };

  const renderSubmenuItem = (
    subItem: typeof fleetSubmenu[0],
    colors: typeof SECTION_COLORS.default,
  ) => {
    const isSelected = location.pathname === subItem.path;
    return (
      <ListItemButton
        key={subItem.text}
        sx={{
          pl: 4,
          mx: 1,
          borderRadius: '10px',
          minHeight: 40,
          '&.Mui-selected': {
            backgroundColor: colors.bg,
            '&:hover': {
              backgroundColor: colors.bgHover,
            },
          },
        }}
        selected={isSelected}
        onClick={() => handleNavigate(subItem.path)}
      >
        <ListItemIcon
          sx={{
            minWidth: 32,
            color: isSelected ? colors.main : 'text.secondary',
          }}
        >
          {subItem.icon}
        </ListItemIcon>
        <ListItemText
          primary={subItem.text}
          sx={{
            '& .MuiListItemText-primary': {
              fontSize: '0.85rem',
              color: isSelected ? colors.main : 'text.secondary',
              fontWeight: isSelected ? 600 : 400,
            },
          }}
        />
      </ListItemButton>
    );
  };

  const renderExpandableSection = (
    item: typeof menuItems[0],
    colors: typeof SECTION_COLORS.default,
    isOpen: boolean,
    onClick: () => void,
    submenu: typeof fleetSubmenu,
  ) => {
    const isActive = location.pathname.startsWith(item.path);

    const content = (
      <ListItemButton
        onClick={onClick}
        selected={isActive}
        sx={{
          mx: 1,
          borderRadius: '10px',
          minHeight: 44,
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          px: isCollapsed ? 1.5 : 2,
          '&.Mui-selected': {
            backgroundColor: colors.bg,
            '&:hover': {
              backgroundColor: colors.bgHover,
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            color: isActive ? colors.main : 'inherit',
            minWidth: isCollapsed ? 0 : 40,
            justifyContent: 'center',
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            transition={{ duration: 0.2 }}
            style={{ display: 'flex', alignItems: 'center', flex: 1 }}
          >
            <ListItemText
              primary={item.text}
              sx={{
                '& .MuiListItemText-primary': {
                  color: isActive ? colors.main : 'inherit',
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: 'nowrap',
                },
              }}
            />
            <Box
              component={motion.div}
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <ExpandMore sx={{ color: 'text.secondary', fontSize: 20 }} />
            </Box>
          </motion.div>
        )}
      </ListItemButton>
    );

    return (
      <React.Fragment key={item.text}>
        {isCollapsed ? (
          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  {item.text}
                </Typography>
                {submenu.map((sub) => (
                  <Typography
                    key={sub.path}
                    variant="caption"
                    display="block"
                    sx={{
                      cursor: 'pointer',
                      py: 0.5,
                      '&:hover': { color: colors.main },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigate(sub.path);
                    }}
                  >
                    {sub.text}
                  </Typography>
                ))}
              </Box>
            }
            placement="right"
            arrow
          >
            {content}
          </Tooltip>
        ) : (
          content
        )}
        {!isCollapsed && (
          <Collapse in={isOpen} timeout={200} unmountOnExit>
            <List component="div" disablePadding>
              {submenu.map((subItem) => renderSubmenuItem(subItem, colors))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: 'rgba(20, 20, 20, 0.95)',
        backdropFilter: 'blur(20px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: isCollapsed ? 1.5 : 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          minHeight: 64,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SidebarToggle />
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Typography
                variant="h6"
                sx={{
                  background: 'linear-gradient(135deg, #4a9eff 0%, #6366f1 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 800,
                  letterSpacing: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                JUST PLAY
              </Typography>
            </motion.div>
          )}
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
        <List sx={{ pt: 1 }}>
          {menuItems.map((item) => {
            if (item.text === 'Scheduling') {
              return renderExpandableSection(
                item,
                SECTION_COLORS.scheduling,
                schedulingOpen,
                handleSchedulingClick,
                schedulingSubmenu,
              );
            }

            if (item.text === 'Fleet') {
              return renderExpandableSection(
                item,
                SECTION_COLORS.fleet,
                fleetOpen,
                handleFleetClick,
                fleetSubmenu,
              );
            }

            if (item.text === 'Management') {
              return renderExpandableSection(
                item,
                SECTION_COLORS.management,
                managementOpen,
                handleManagementClick,
                managementSubmenu,
              );
            }

            // Use aiGeneration colors for SongForge
            const colors = item.text === 'SongForge' ? SECTION_COLORS.aiGeneration : SECTION_COLORS.default;
            const isSelected = item.text === 'SongForge'
              ? location.pathname.startsWith('/songforge')
              : location.pathname === item.path;

            return (
              <React.Fragment key={item.text}>
                {renderMenuItem(item, colors, isSelected)}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* Footer */}
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.06)' }} />
      <Box
        sx={{
          p: isCollapsed ? 1 : 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: isCollapsed ? 'center' : 'flex-start',
          gap: 0.5,
        }}
      >
        {!isCollapsed && !APP_CONFIG.isProduction && (
          <Chip
            label={APP_CONFIG.environment.toUpperCase()}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              backgroundColor: APP_CONFIG.isStaging ? 'warning.main' : 'info.main',
              color: APP_CONFIG.isStaging ? 'warning.contrastText' : 'info.contrastText',
            }}
          />
        )}
        <Tooltip title={isCollapsed ? `v${APP_CONFIG.version} (${APP_CONFIG.buildSha})` : ''} placement="right">
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isCollapsed ? `v${APP_CONFIG.version.split('.')[0]}` : `eBGM v${APP_CONFIG.version}`}
          </Typography>
        </Tooltip>
        {!isCollapsed && (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
            build: {APP_CONFIG.buildSha}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Sidebar;
